<template>
  <div class="session-manager-page">
    <WelcomeBanner title="会话管理" subtitle="管理与审阅会话">
      <div slot="right">
        <el-button :type="viewType === 'list' ? 'primary' : ''" icon="el-icon-tickets" size="small" @click="viewType = 'list'">列表</el-button>
        <el-button :type="viewType === 'card' ? 'primary' : ''" icon="el-icon-menu" size="small" @click="viewType = 'card'">卡片</el-button>
        <el-button type="success" icon="el-icon-plus" size="small" @click="showCreateDialog">新建会话</el-button>
      </div>
    </WelcomeBanner>

    <BaseCard>
      <div slot="header" class="filter-bar">
      <el-select v-model="filters.projectId" placeholder="项目筛选" clearable style="width: 160px;" @change="loadSessions">
        <el-option label="全部项目" value="" />
        <el-option v-for="project in projectList" :key="project.project_id" :label="project.project_name" :value="project.project_id" />
      </el-select>
      <el-select v-model="filters.sessionType" placeholder="类型筛选" clearable style="width: 140px;">
        <el-option label="全部类型" value="" />
        <el-option label="执行会话" value="1" />
        <el-option label="协调会话" value="2" />
        <el-option label="反馈会话" value="3" />
        <el-option label="其他" value="4" />
      </el-select>
      <el-select v-model="filters.status" placeholder="状态筛选" clearable style="width: 120px;">
        <el-option label="全部状态" value="" />
        <el-option label="已关闭" value="0" />
        <el-option label="待处理" value="1" />
        <el-option label="处理中" value="2" />
        <el-option label="已处理" value="3" />
        <el-option label="已过期" value="4" />
      </el-select>
      <el-input v-model="keywords" placeholder="搜索会话内容" clearable style="width: 220px;">
        <el-button slot="append" icon="el-icon-search" @click="loadSessions"></el-button>
      </el-input>
      </div>

      <div v-loading="loading" class="content-area">
      <!-- 列表视图 -->
      <div v-if="viewType === 'list'" class="list-view">
        <el-table :data="sessionList" style="width: 100%" stripe>
          <el-table-column prop="session_id" label="会话ID" width="100" show-overflow-tooltip />
          <el-table-column prop="chat_type" label="类型" width="90">
            <template slot-scope="scope">
              <el-tag size="small" effect="plain">{{ getSessionTypeName(scope.row.chat_type) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="content" label="会话内容" min-width="250" show-overflow-tooltip />
          <el-table-column prop="status" label="状态" width="80" align="center">
            <template slot-scope="scope">
              <el-tag :type="getStatusTagType(scope.row.status)" size="small">{{ getStatusText(scope.row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="sender_id" label="发送方" width="100" show-overflow-tooltip />
          <el-table-column prop="receiver_id" label="接收方" width="100" show-overflow-tooltip />
          <el-table-column prop="project_id" label="所属项目" width="140" show-overflow-tooltip>
            <template slot-scope="scope">{{ getProjectName(scope.row.project_id) }}</template>
          </el-table-column>
          <el-table-column prop="create_time" label="创建时间" width="140">
            <template slot-scope="scope">{{ formatTime(scope.row.create_time) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="150" fixed="right" align="center">
            <template slot-scope="scope">
              <el-button type="text" size="small" @click="goToDetail(scope.row.session_id)">查看</el-button>
              <el-button type="text" size="small" @click="showEditDialog(scope.row)">编辑</el-button>
              <el-button type="text" size="small" class="danger" @click="deleteSession(scope.row.session_id)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.currentPage"
            :page-sizes="[10, 20, 50]"
            :page-size="pagination.pageSize"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
          ></el-pagination>
        </div>
      </div>

      <!-- 卡片视图 -->
      <div v-if="viewType === 'card'" class="card-view">
        <el-row :gutter="20">
          <el-col :span="8" v-for="item in sessionList" :key="item.session_id">
            <el-card class="session-card" shadow="hover">
              <div class="card-header">
                <div class="session-type">
                  <el-tag size="small" effect="plain">{{ getSessionTypeName(item.chat_type) }}</el-tag>
                </div>
                <el-tag :type="getStatusTagType(item.status)" size="mini">{{ getStatusText(item.status) }}</el-tag>
              </div>
              <div class="card-body">
                <p class="session-content" :title="item.content">{{ item.content || '无内容' }}</p>
                <div class="meta-info">
                  <div class="meta-item">
                    <i class="el-icon-user"></i>
                    <span>{{ item.sender_id }}</span>
                    <i class="el-icon-arrow-right" style="margin: 0 4px;"></i>
                    <span>{{ item.receiver_id }}</span>
                  </div>
                  <div class="meta-item" v-if="item.project_id">
                    <i class="el-icon-folder"></i>
                    <span>{{ getProjectName(item.project_id) }}</span>
                  </div>
                </div>
              </div>
              <div class="card-footer">
                <span class="create-time"><i class="el-icon-time"></i> {{ formatTime(item.create_time) }}</span>
                <div class="actions">
                  <el-button type="text" size="small" @click="goToDetail(item.session_id)">查看</el-button>
                  <el-button type="text" size="small" @click="showEditDialog(item)">编辑</el-button>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <div class="pagination">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.currentPage"
            :page-sizes="[10, 20, 50]"
            :page-size="pagination.pageSize"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
          ></el-pagination>
        </div>
      </div>

      <div v-if="!sessionList.length && !loading" class="empty-state">
        <i class="el-icon-chat-dot-square" style="font-size: 64px; color: #dcdfe6;"></i>
        <p>暂无会话数据</p>
        <el-button type="primary" size="small" @click="showCreateDialog" style="margin-top: 16px;">创建会话</el-button>
      </div>
    </div>

    </BaseCard>

    <!-- 新建会话对话框 -->
    <el-dialog title="新建会话" :visible.sync="createDialogVisible" width="600px">
      <el-form :model="newSession" :rules="createRules" ref="createForm">
        <el-form-item label="会话类型" prop="sessionType">
          <el-select v-model="newSession.sessionType" placeholder="请选择会话类型">
            <el-option label="执行会话" value="1" />
            <el-option label="协调会话" value="2" />
            <el-option label="反馈会话" value="3" />
            <el-option label="其他" value="4" />
          </el-select>
        </el-form-item>
        <el-form-item label="会话内容" prop="sessionContent">
          <el-input type="textarea" v-model="newSession.sessionContent" placeholder="请输入会话内容" :rows="4"></el-input>
        </el-form-item>
        <el-form-item label="发送方" prop="sender">
          <el-input v-model="newSession.sender" placeholder="请输入发送方"></el-input>
        </el-form-item>
        <el-form-item label="接收方" prop="receiver">
          <el-input v-model="newSession.receiver" placeholder="请输入接收方"></el-input>
        </el-form-item>
        <el-form-item label="所属项目" prop="projectId">
          <el-select v-model="newSession.projectId" placeholder="请选择所属项目" style="width: 100%;">
            <el-option label="无项目" value="" />
            <el-option v-for="project in projectList" :key="project.project_id" :label="project.project_name" :value="project.project_id" />
          </el-select>
        </el-form-item>
        <el-form-item label="截止时间" prop="dueTime">
          <el-date-picker
            v-model="newSession.dueTime"
            type="datetime"
            placeholder="选择截止时间"
            style="width: 100%"
          ></el-date-picker>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="createSession">创建</el-button>
      </div>
    </el-dialog>

    <!-- 编辑会话对话框 -->
    <el-dialog title="编辑会话" :visible.sync="editDialogVisible" width="600px">
      <el-form :model="editSession" :rules="editRules" ref="editForm">
        <el-form-item label="会话类型" prop="sessionType">
          <el-select v-model="editSession.sessionType" placeholder="请选择会话类型">
            <el-option label="执行会话" value="1" />
            <el-option label="协调会话" value="2" />
            <el-option label="反馈会话" value="3" />
            <el-option label="其他" value="4" />
          </el-select>
        </el-form-item>
        <el-form-item label="会话内容" prop="sessionContent">
          <el-input type="textarea" v-model="editSession.sessionContent" placeholder="请输入会话内容" :rows="4"></el-input>
        </el-form-item>
        <el-form-item label="发送方" prop="sender">
          <el-input v-model="editSession.sender" placeholder="请输入发送方"></el-input>
        </el-form-item>
        <el-form-item label="接收方" prop="receiver">
          <el-input v-model="editSession.receiver" placeholder="请输入接收方"></el-input>
        </el-form-item>
        <el-form-item label="所属项目" prop="projectId">
          <el-select v-model="editSession.projectId" placeholder="请选择所属项目">
            <el-option label="无项目" value="" />
            <el-option v-for="project in projectList" :key="project.project_id" :label="project.project_name" :value="project.project_id" />
          </el-select>
        </el-form-item>
        <el-form-item label="截止时间" prop="dueTime">
          <el-date-picker
            v-model="editSession.dueTime"
            type="datetime"
            placeholder="选择截止时间"
            style="width: 100%"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select v-model="editSession.status" placeholder="请选择状态">
            <el-option label="已关闭" value="0" />
            <el-option label="待处理" value="1" />
            <el-option label="处理中" value="2" />
            <el-option label="已处理" value="3" />
            <el-option label="已过期" value="4" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="updateSession">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getSessionListPaged, getMySessionsPaged, createSession, closeSession, deleteSession } from '../api/session'
import { getMyProjectList } from '../api/project'
import BaseCard from '@/components/BaseCard'
import WelcomeBanner from '@/components/WelcomeBanner'

export default {
  name: 'SessionManager',
  components: { BaseCard, WelcomeBanner },
  data() {
    return {
      loading: false,
      sessionList: [],
      projectList: [],
      viewType: 'list',
      keywords: '',
      filters: {
        projectId: '',
        sessionType: '',
        status: ''
      },
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      createDialogVisible: false,
      editDialogVisible: false,
      newSession: {
        sessionType: '1',
        sessionContent: '',
        sender: '',
        receiver: '',
        projectId: '',
        dueTime: ''
      },
      editSession: {
        sessionId: '',
        sessionType: '1',
        sessionContent: '',
        sender: '',
        receiver: '',
        projectId: '',
        dueTime: '',
        status: '1'
      },
      createRules: {
        sessionType: [{ required: true, message: '请选择会话类型', trigger: 'change' }],
        sessionContent: [{ required: true, message: '请输入会话内容', trigger: 'blur' }],
        sender: [{ required: true, message: '请输入发送方', trigger: 'blur' }],
        receiver: [{ required: true, message: '请输入接收方', trigger: 'blur' }],
        projectId: [{ required: false }],
        dueTime: [{ required: false }]
      },
      editRules: {
        sessionType: [{ required: true, message: '请选择会话类型', trigger: 'change' }],
        sessionContent: [{ required: true, message: '请输入会话内容', trigger: 'blur' }],
        sender: [{ required: true, message: '请输入发送方', trigger: 'blur' }],
        receiver: [{ required: true, message: '请输入接收方', trigger: 'blur' }],
        projectId: [{ required: false }],
        dueTime: [{ required: false }],
        status: [{ required: true, message: '请选择状态', trigger: 'change' }]
      }
    }
  },
  async created() {
    // 先加载项目列表，再加载会话（loadSessions 会用到项目列表）
    await this.loadProjects()
    await this.loadSessions()
  },
  methods: {
    async loadSessions() {
      this.loading = true
      try {
        // 使用新的接口一次性获取当前用户的所有会话
        const params = {
          page: this.pagination.currentPage,
          page_size: this.pagination.pageSize,
          project_id: this.filters.projectId || undefined
        }
        if (this.filters.status) params.status = this.filters.status
        
        const data = await getMySessionsPaged(params)
        this.sessionList = data?.list || []
        this.pagination.total = data?.total || 0
      } catch (error) {
        console.error('获取会话列表失败:', error)
        this.$message.error(`获取会话列表失败: ${error.message || '未知错误'}`)
        this.sessionList = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },
    async loadProjects() {
      try {
        const data = await getMyProjectList()
        this.projectList = data?.list || []
      } catch (error) {
        console.error('获取项目列表失败:', error)
        // 不显示错误提示，因为这是正常的业务场景（没有项目）
        this.projectList = []
      }
    },
    getProjectName(projectId) {
      if (!projectId) return '未分配'
      // 优先使用会话中携带的 project_name 字段
      const session = this.sessionList.find(s => s.project_id === projectId && s.project_name)
      if (session && session.project_name) {
        return session.project_name
      }
      // 回退到从项目列表查找
      const project = this.projectList.find(p => p.project_id === projectId)
      return project ? project.project_name : projectId
    },
    getSessionTypeName(type) {
      const typeMap = {
        '1': '执行会话',
        '2': '协调会话',
        '3': '反馈会话',
        '4': '其他'
      }
      return typeMap[type] || type
    },
    getStatusText(status) {
      const statusMap = {
        '0': '已关闭',
        '1': '待处理',
        '2': '处理中',
        '3': '已处理',
        '4': '已过期'
      }
      return statusMap[status] || status
    },
    getStatusClass(status) {
      const classMap = {
        '1': 'status-pending',
        '2': 'status-processing',
        '3': 'status-processed',
        '4': 'status-expired'
      }
      return classMap[status] || 'status-pending'
    },
    getStatusTagType(status) {
      const typeMap = {
        '1': 'warning',
        '2': 'primary',
        '3': 'success',
        '4': 'danger'
      }
      return typeMap[status] || 'info'
    },
    showCreateDialog() {
      this.createDialogVisible = true
      this.newSession = {
        sessionType: '1',
        sessionContent: '',
        sender: '',
        receiver: '',
        projectId: '',
        dueTime: ''
      }
    },
    async createSession() {
      try {
        // 转换字段格式
        const data = {
          session_type: parseInt(this.newSession.sessionType) || 1,
          session_content: this.newSession.sessionContent,
          sender: this.newSession.sender,
          receiver: this.newSession.receiver,
          project_id: this.newSession.projectId || null,
          due_time: this.newSession.dueTime ? new Date(this.newSession.dueTime).toISOString() : null
        }
        await createSession(data)
        this.createDialogVisible = false
        this.$message.success('会话创建成功')
        this.loadSessions()
      } catch (error) {
        console.error('创建会话失败:', error)
        this.$message.error(`创建失败: ${error.message || '未知错误'}`)
      }
    },
    showEditDialog(session) {
      // 将后端下划线命名字段映射到前端表单字段
      this.editSession = {
        sessionId: session.session_id,
        sessionType: String(session.chat_type || 1),
        sessionContent: session.content,
        sender: session.sender_id,
        receiver: session.receiver_id,
        projectId: session.project_id,
        dueTime: session.expire_time,
        status: String(session.status || 1)
      }
      this.editDialogVisible = true
    },
    async updateSession() {
      try {
        await closeSession(this.editSession.sessionId)
        await createSession({
          session_type: parseInt(this.editSession.sessionType),
          session_content: this.editSession.sessionContent,
          sender: this.editSession.sender,
          receiver: this.editSession.receiver,
          project_id: this.editSession.projectId,
          due_time: this.editSession.dueTime,
          status: parseInt(this.editSession.status)
        })
        this.editDialogVisible = false
        this.$message.success('会话更新成功')
        this.loadSessions()
      } catch (error) {
        console.error('更新会话失败:', error)
        this.$message.error(`更新失败: ${error.message || '未知错误'}`)
      }
    },
    async deleteSession(id) {
      try {
        await this.$confirm('确定要删除该会话吗？', '提示', { type: 'warning' })
        await deleteSession(id)
        this.$message.success('删除成功')
        this.loadSessions()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除会话失败:', error)
          this.$message.error(`删除失败: ${error.message || '未知错误'}`)
        }
      }
    },
    formatTime(time) {
      if (!time) return '-'
      return new Date(time).toLocaleString('zh-CN')
    },
    goToDetail(sessionId) {
      this.$router.push({ name: 'SessionDetail', params: { id: sessionId } })
    },
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.loadSessions()
    },
    handleCurrentChange(page) {
      this.pagination.currentPage = page
      this.loadSessions()
    }
  }
}
</script>

<style lang="scss" scoped>
.session-manager-page {
  padding: 16px;
  min-height: calc(100vh - 100px);
  background: linear-gradient(180deg, #f5f7fa 0%, #fff 100%);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  background: linear-gradient(135deg, #818cf8 0%, #a78bfa 100%);
  border-radius: 10px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(129, 140, 248, 0.25);
}

.page-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.header-actions {
  display: flex;
  gap: 8px;

  .el-button {
    height: 36px;
    align-items: center;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    border: none;
  }

  .el-button:hover { background: rgba(255,255,255,0.18); }

  .el-button--primary { background: rgba(255,255,255,0.22); color: #fff; }
}

.filter-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
  padding: 16px 20px;
  background-color: var(--bg-card);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.content-area {
  background-color: var(--bg-card);
  border-radius: var(--radius-lg);
  padding: 20px;
  box-shadow: var(--shadow-sm);
  min-height: 400px;
}

/* 列表视图 */
.list-view {
  width: 100%;
}

/* 卡片视图 */
.card-view {
  width: 100%;
}

.session-card {
  margin-bottom: 20px;
  border-radius: var(--radius-lg);
  transition: all 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }

  :deep(.el-card__body) {
    padding: 16px;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .card-body {
    margin-bottom: 12px;
  }
}

.session-type {
  font-weight: 500;
}

.session-content {
  margin: 0 0 12px 0;
  color: var(--text-primary);
  font-size: 14px;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  min-height: 42px;
}

.meta-info {
  font-size: 12px;
  color: var(--text-muted);
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;

  i {
    font-size: 12px;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid var(--border-color);
}

.create-time {
  font-size: 12px;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 4px;

  i {
    font-size: 12px;
  }
}

.actions {
  display: flex;
  gap: 8px;
}

/* 分页 */
.pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--border-color);
}

/* 空状态 */
.empty-state {
  text-align: center;
  color: var(--text-muted);
  padding: 80px 20px;

  p {
    margin-top: 16px;
    font-size: 14px;
  }
}

/* 危险按钮 */
.danger {
  color: var(--danger-color);

  &:hover {
    color: var(--danger-light);
  }
}
</style>
